import { useAppStateDispatch, useAppStateSelector } from '@src/providers/AppStateProvider';
import type { AppState } from '@src/providers/AppStateProvider/types';
import {
  Box,
  Button,
  ButtonVariants,
  Flex,
  FormControlSizes,
  HStack,
  IconName,
  Input,
  Text,
  Tooltip,
  VStack,
  type Security,
} from '@talos/kyoko';
import { noop, startCase } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import type { MultilegComboType } from './enums';
import {
  multilegComboSliceActions,
  selectEditableProperties,
  selectInstrumentField,
  selectIsEditing,
} from './MultilegComboSlice';
import type { GroupableSelectItem } from './types';

const getLabel = (item: GroupableSelectItem<MultilegComboType>) => item.label;

export function MultilegComboDetails({
  security,
  isModal,
  panelID,
}: {
  security: Security | undefined;
  isModal?: boolean;
  panelID: string;
}) {
  const dispatch = useAppStateDispatch();
  const isEditing = useAppStateSelector((state: AppState) => selectIsEditing(state, panelID));
  const editableProperties = useAppStateSelector((state: AppState) => selectEditableProperties(state, panelID));
  const instrumentField = useAppStateSelector((state: AppState) => selectInstrumentField(state, panelID));

  const handleChangeInstrument = useCallback(
    (value: GroupableSelectItem<MultilegComboType> | undefined) => {
      if (value != null) {
        dispatch(multilegComboSliceActions.updateInstrument({ panelID, instrument: value }));
      }
    },
    [dispatch, panelID]
  );

  const instrumentItemsToShow = useMemo(() => {
    if (!instrumentField) {
      return [];
    }
    return isEditing || instrumentField.isDisabled
      ? instrumentField.availableItems.filter(item => instrumentField.value?.value === item.value)
      : instrumentField.availableItems;
  }, [instrumentField, isEditing]);

  return (
    <>
      <HStack gap="spacing8" data-testid="multileg-instrument-selector">
        <span>Multileg Type:</span>
        {instrumentItemsToShow.map(item => (
          <Flex flex="1 1 45px" key={item.value}>
            <Button
              variant={instrumentField.value?.value === item.value ? ButtonVariants.Primary : ButtonVariants.Default}
              size={isModal ? FormControlSizes.Default : FormControlSizes.Small}
              onClick={() => handleChangeInstrument(item)}
              width="100%"
              data-testid={`multileg-instrument-button-${item.value}`}
              disabled={instrumentField.isDisabled || isEditing}
            >
              <Text>{getLabel(item)}</Text>
            </Button>
          </Flex>
        ))}
      </HStack>
      {!security ? null : isEditing ? (
        <HStack gap="spacing12">
          <Box w={350}>
            <Input
              prefix="Description"
              value={editableProperties.description ?? security.Description}
              size={isModal ? FormControlSizes.Default : FormControlSizes.Small}
              data-testid="multileg-description-input"
              onChange={e =>
                isEditing
                  ? dispatch(
                      multilegComboSliceActions.updateEditableProperty({
                        key: 'description',
                        value: e.target.value,
                        panelID,
                      })
                    )
                  : null
              }
            />
          </Box>
          <Box w={isModal ? undefined : 220}>
            <Input
              prefix="Min Price Increment"
              value={editableProperties.minPriceIncrement || security.MinPriceIncrement}
              size={isModal ? FormControlSizes.Default : FormControlSizes.Small}
              type="number"
              data-testid="multileg-min-price-increment-input"
              onChange={e =>
                isEditing
                  ? dispatch(
                      multilegComboSliceActions.updateEditableProperty({
                        key: 'minPriceIncrement',
                        value: e.target.value,
                        panelID,
                      })
                    )
                  : null
              }
            />
          </Box>
          <Box w={isModal ? undefined : 220}>
            <Input
              prefix="Min Size Increment"
              value={editableProperties.minSizeIncrement || security.MinSizeIncrement}
              size={isModal ? FormControlSizes.Default : FormControlSizes.Small}
              type="number"
              data-testid="multileg-min-size-increment-input"
              onChange={e =>
                isEditing
                  ? dispatch(
                      multilegComboSliceActions.updateEditableProperty({
                        key: 'minSizeIncrement',
                        value: e.target.value,
                        panelID,
                      })
                    )
                  : null
              }
            />
          </Box>
          <Box w={isModal ? undefined : 220}>
            <Input
              prefix="Max Market Data Depth"
              value={editableProperties.maxMarketDataDepth || security?.MultilegDetails?.MaxMarketDataDepth}
              size={isModal ? FormControlSizes.Default : FormControlSizes.Small}
              type="number"
              data-testid="multileg-max-market-data-depth-input"
              onChange={e =>
                isEditing
                  ? dispatch(
                      multilegComboSliceActions.updateEditableProperty({
                        key: 'maxMarketDataDepth',
                        value: e.target.value,
                        panelID,
                      })
                    )
                  : null
              }
            />
          </Box>
        </HStack>
      ) : (
        <Tooltip
          trigger="click"
          closeOnClickOutside={true}
          placement="bottom-end"
          contentMaxWidth="100%"
          usePortal={false}
          tooltip={
            <VStack gap="spacing8" justifyContent="flex-start" alignItems="stretch">
              {(['Description', 'MinPriceIncrement', 'MinSizeIncrement'] as const).map(key => (
                <HStack key={key} justifyContent="space-between" gap="spacing24">
                  <Text color="colorTextSubtle" size="fontSizeSm">
                    {startCase(key)}
                  </Text>
                  <Text color="colorTextImportant" size="fontSizeSm">
                    {security?.[key]}
                  </Text>
                </HStack>
              ))}
              <HStack key="MaxMarketDataDepth" justifyContent="space-between" gap="spacing24">
                <Text color="colorTextSubtle" size="fontSizeSm">
                  Max Market Data Depth
                </Text>
                <Text color="colorTextImportant" size="fontSizeSm">
                  {security?.MultilegDetails?.MaxMarketDataDepth}
                </Text>
              </HStack>
            </VStack>
          }
        >
          <Button onClick={noop} size={FormControlSizes.Tiny} endIcon={IconName.InformationCircleSolid}>
            Details
          </Button>
        </Tooltip>
      )}
    </>
  );
}
